class SliderComponent extends HTMLElement {
  constructor() {
    super();

    this.init();
  }

  init() {
    this.slider = this.classList.contains("slideshow")
      ? this
      : this.querySelector('[id^="Slider-"]');

    this.sliderItems = Array.from(this.querySelectorAll('[id^="Slide-"]'));
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector(".slider-counter--current");
    this.pageTotalElement = this.querySelector(".slider-counter--total");
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.enableDrag = this.dataset.draggable === "true";
    
    if (!this.slider) return;

    // Cache some expensive calculations
    this.sliderRect = this.slider.getBoundingClientRect();
    this.sliderFirstItem = this.sliderItems[0];
    this.sliderSecondItem = this.sliderItems[1];

    this.initPages();
    // Debounce the resize observer to prevent frequent calls to initPages
    const resizeObserver = new ResizeObserver(this.debounce(() => this.initPages(), 200));
    resizeObserver.observe(this.slider);

    // Add event listeners only once
    this.slider.addEventListener("scroll", this.update.bind(this));
    this.prevButton?.addEventListener("click", this.onButtonClick.bind(this));
    this.nextButton?.addEventListener("click", this.onButtonClick.bind(this));

    if (this.enableDrag) {
      this.addClickAndDrag();
    }
  }

  // Debounce function to limit the frequency of a function being called
  debounce(func, delay) {
    let timeout;
    return function (...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), delay);
    };
  }

  initPages() {
    // Cache DOM values and calculations for performance
    this.sliderItemsToShow = this.sliderItems.filter((element) => element.clientWidth > 0);

    if (this.sliderItemsToShow.length < 2) return;

    // Cache the offsetLeft calculations
    const firstItemOffsetLeft = this.sliderItemsToShow[0].offsetLeft;
    const secondItemOffsetLeft = this.sliderItemsToShow[1].offsetLeft;

    this.sliderItemOffset = secondItemOffsetLeft - firstItemOffsetLeft;
    this.slidesPerPage = Math.floor((this.slider.clientWidth - firstItemOffsetLeft) / this.sliderItemOffset);
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;

    // Call the update method to refresh the state of the buttons and pages
    this.update();
  }

  update() {
    if (!this.slider) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    // Trigger the slideChanged event only if the page has actually changed
    if (this.currentPage !== previousPage) {
      this.dispatchEvent(
        new CustomEvent("slideChanged", {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    // Disable/enable prev/next buttons based on the current page
    if (!this.enableSliderLooping) {
      const isAtStart = this.slider.scrollLeft === 0;
      const isAtEnd = this.slider.scrollLeft + this.slider.clientWidth >= this.slider.scrollWidth;

      if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
        this.prevButton?.setAttribute("disabled", "disabled");
        if (this.prevButton && isAtStart == true && isAtEnd == true)
          this.prevButton.style.display = "none";
      } else {
        this.prevButton?.removeAttribute("disabled");
        if (this.prevButton)
          this.prevButton.style.display = "flex";
      }

      if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
        this.nextButton?.setAttribute("disabled", "disabled");
        if (this.nextButton && isAtStart == true && isAtEnd == true)
          this.nextButton.style.display = "none";
      } else {
        this.nextButton?.removeAttribute("disabled");
        if (this.nextButton)
          this.nextButton.style.display = "flex";
      }
    }
  }

  isSlideVisible(element) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = parseInt(event.currentTarget.dataset.step) || 1;
    this.slideScrollPosition =
      event.currentTarget.name === "next"
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;

    this.slider.scrollTo({
      left: this.slideScrollPosition,
    });
  }

  addClickAndDrag() {
    let isDown = false;
    let startX;
    let startScrollLeft;
    let preventClick = false;

    this.slider.addEventListener("mousedown", (e) => {
      if (window.innerWidth <= 749) return;
      isDown = true;
      startX = e.pageX;
      startScrollLeft = this.slider.scrollLeft;
      this.slider.style.scrollSnapType = "none";
      this.slider.style.scrollBehavior = "auto";
      e.preventDefault();
    });

    this.slider.addEventListener("mouseup", () => {
      if (window.innerWidth <= 749) return;
      if (isDown) {
        isDown = false;
        this.slider.style.scrollBehavior = "";
        this.slider.style.scrollSnapType = "";
      }
      setTimeout(() => preventClick = false, 0);
    });

    this.slider.addEventListener("mousemove", (e) => {
      if (window.innerWidth <= 749 || !isDown) return;
      const x = e.pageX;
      const walk = x - startX;
      this.slider.scrollLeft = startScrollLeft - walk;
      preventClick = true;
    });

    this.slider.addEventListener("click", (e) => {
      if (preventClick) e.preventDefault();
    });
  }


  connectedCallback() {
    if (this.dataset.reset_scroll === "true") {
      this.slider.scrollLeft = 0;
    }
  }
}

customElements.define("slider-component", SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector(".slider-buttons");
    if (!this.dataset.enableSliderLooping == 'false') this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll(".slider-counter__link"));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener("click", this.linkToSlide.bind(this)));

    this.slider.addEventListener("scroll", this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.getAttribute("data-autoplay") === "true") this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.play();
  }

  setSlideVisibility() {
    this.sliderItemsToShow.forEach((item, index) => {
      const isActive = index === this.currentPage - 1;
      const focusables = item.querySelectorAll(
        'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      
      if (focusables.length === 0) {
        item.setAttribute("aria-hidden", !isActive);
        item.setAttribute("tabindex", isActive ? "0" : "-1");
      }
    });
  }

  linkToSlide(event) {
    event.preventDefault();
    const targetIndex = this.sliderControlLinksArray.indexOf(event.currentTarget);
    const scrollPosition = targetIndex * this.sliderItemsToShow[0].clientWidth;
    this.slider.scrollTo({ left: scrollPosition });
  }
}

customElements.define("slideshow-component", SlideshowComponent);
